import { useCallback, useEffect, useState } from "react";
import { Plug, RefreshCw, KeyRound, CheckCircle2 } from "lucide-react";
import api, { fmtDateTime, fmtErr } from "../../lib/api";
import { toast } from "sonner";

const CRED_FIELDS = {
  shiprocket: [["email", "Account email"], ["password", "Account password"]],
  shipmozo: [["public_key", "Public key"], ["private_key", "Private key"]],
  icarry: [["username", "Username"], ["api_key", "API key"]],
  shipcorrect: [["api_key", "API key"], ["seller_email", "Seller portal email (warehouse sync)"], ["seller_password", "Seller portal password (warehouse sync)"]],
  bigship: [["api_key", "API key"], ["webhook_secret", "Webhook secret"]],
};

export default function AdminProviders() {
  const [providers, setProviders] = useState(null);
  const [testing, setTesting] = useState("");
  const [credFor, setCredFor] = useState(null);
  const [credForm, setCredForm] = useState({});
  const [savingCreds, setSavingCreds] = useState(false);

  const saveCreds = async () => {
    setSavingCreds(true);
    try {
      const { data } = await api.put(`/admin/providers/${credFor}/credentials`, { secrets: credForm });
      if (data.health_error) toast.warning(`Saved, but live check failed: ${data.health_error}`, { duration: 12000 });
      else toast.success("Credentials saved — live connection verified");
      setCredFor(null);
      setCredForm({});
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setSavingCreds(false); }
  };

  const load = useCallback(() => api.get("/admin/providers").then(({ data }) => setProviders(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (p) => {
    try {
      await api.put(`/admin/providers/${p.code}`, { enabled: !p.enabled });
      toast.success(`${p.name} ${p.enabled ? "disabled" : "enabled"}`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const setPriority = async (p, priority) => {
    try {
      await api.put(`/admin/providers/${p.code}`, { priority: Number(priority) });
      toast.success(`${p.name} priority → ${priority}`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const test = async (p) => {
    setTesting(p.code);
    try {
      const { data } = await api.post(`/admin/providers/${p.code}/test`);
      toast.success(`${p.name} healthy — ${data.latency_ms}ms (${data.mode} mode)`);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setTesting("");
    }
  };

  const toggleLive = async (p) => {
    try {
      const { data } = await api.post(`/admin/providers/${p.code}/live`, { live_enabled: !p.live_enabled });
      toast.success(data.live_enabled ? `${p.name} is LIVE (real API)` : `${p.name} paused — bookings will skip it`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const toggleOutage = async (p) => {
    await api.post(`/admin/providers/${p.code}/simulate-outage`, { on: !p.simulate_outage });
    toast.success(p.simulate_outage ? `${p.name} outage simulation off` : `${p.name} simulating outage — bookings will fail over`);
    load();
  };

  return (
    <div data-testid="admin-providers-page">
      <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Providers</h1>
      <p className="text-sm text-slate-500">Carrier aggregators behind the SHIPANY adapter layer. Credentials stay server-side.</p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="providers-grid">
        {(providers || []).map((p) => (
          <div key={p.code} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`provider-card-${p.code}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-shipany-navy text-shipany-cyan"><Plug className="h-5 w-5" /></span>
                <div>
                  <h3 className="font-display font-bold text-shipany-navy">{p.name}</h3>
                  <p className="mono text-xs text-slate-400">{p.code} · {p.live_enabled ? <span className="font-bold text-emerald-600">LIVE API</span> : "standby"}{p.simulate_outage && <span className="ml-1 font-bold text-red-500">· OUTAGE SIM</span>}</p>
                </div>
              </div>
              <button onClick={() => toggle(p)} data-testid={`provider-toggle-${p.code}`}
                className={`relative h-6 w-11 rounded-full transition-colors ${p.enabled ? "bg-emerald-500" : "bg-slate-300"}`}>
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${p.enabled ? "left-[22px]" : "left-0.5"}`} />
              </button>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3 text-xs">
              <div className="rounded-lg bg-slate-50 p-3"><p className="text-slate-400">Status</p>
                <p className={`mt-1 font-bold ${p.enabled ? "text-emerald-600" : "text-slate-500"}`}>{p.enabled ? "Enabled" : "Disabled"}</p></div>
              <div className="rounded-lg bg-slate-50 p-3"><p className="text-slate-400">Latency</p>
                <p className="mono mt-1 font-bold text-shipany-blue">{p.health?.latency_ms ? `${p.health.latency_ms} ms` : "—"}</p></div>
              <div className="rounded-lg bg-slate-50 p-3"><p className="text-slate-400">Couriers</p>
                <p className="mono mt-1 font-bold text-shipany-navy">{p.courier_count}</p></div>
              <div className="rounded-lg bg-slate-50 p-3"><p className="text-slate-400">Credentials</p>
                <p className="mt-1 flex items-center gap-1 font-bold">{p.credentials_configured
                  ? <><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /><span className="text-emerald-600">Configured</span></>
                  : <><KeyRound className="h-3.5 w-3.5 text-amber-500" /><span className="text-amber-600">Not connected</span></>}</p></div>
            </div>

            <div className="mt-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                Priority
                <select value={p.priority} onChange={(e) => setPriority(p, e.target.value)} data-testid={`provider-priority-${p.code}`}
                  className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs font-bold focus:border-shipany-blue focus:outline-none">
                  {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>P{n}</option>)}
                </select>
              </div>
              <button onClick={() => test(p)} disabled={testing === p.code} data-testid={`provider-test-${p.code}`}
                className="flex items-center gap-1.5 rounded-lg bg-shipany-blue px-4 py-2 text-xs font-bold text-white hover:bg-[#0648B3] disabled:opacity-60">
                <RefreshCw className={`h-3.5 w-3.5 ${testing === p.code ? "animate-spin" : ""}`} /> Test Connection
              </button>
              <button onClick={() => { setCredFor(p.code); setCredForm({}); }} data-testid={`provider-keys-${p.code}`}
                className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold text-slate-600 hover:border-shipany-blue hover:text-shipany-blue">
                <KeyRound className="h-3.5 w-3.5" /> Update Keys
              </button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button onClick={() => toggleLive(p)} data-testid={`provider-live-${p.code}`}
                className={`rounded-lg px-3 py-1.5 text-[11px] font-bold ${p.live_enabled ? "bg-emerald-600 text-white" : "border border-slate-200 text-slate-500 hover:border-emerald-400 hover:text-emerald-600"}`}>
                {p.live_enabled ? "● LIVE" : "Go Live"}
              </button>
              <button onClick={() => toggleOutage(p)} data-testid={`provider-outage-${p.code}`}
                className={`rounded-lg px-3 py-1.5 text-[11px] font-bold ${p.simulate_outage ? "bg-red-600 text-white" : "border border-slate-200 text-slate-500 hover:border-red-400 hover:text-red-600"}`}>
                {p.simulate_outage ? "● Simulating outage" : "Simulate outage"}
              </button>
            </div>
            <p className="mt-3 text-[10px] text-slate-400">Last request: {fmtDateTime(p.health?.last_request_at)}{p.health?.last_error ? ` · Error: ${p.health.last_error}` : ""}</p>
          </div>
        ))}
      </div>

      {credFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="provider-keys-modal">
          <div className="absolute inset-0 bg-shipany-navy/60 backdrop-blur-sm" onClick={() => setCredFor(null)} />
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6">
            <h3 className="font-display text-lg font-bold capitalize text-shipany-navy">Update {credFor} credentials</h3>
            <p className="mt-1 text-xs text-slate-400">Stored server-side only, never shown again. The live connection is verified immediately after saving.</p>
            <div className="mt-4 space-y-3">
              {(CRED_FIELDS[credFor] || []).map(([k, label]) => (
                <input key={k} data-testid={`cred-${k}`} type="password" placeholder={label} value={credForm[k] || ""}
                  onChange={(e) => setCredForm({ ...credForm, [k]: e.target.value })}
                  className="mono w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-shipany-blue focus:outline-none" />
              ))}
            </div>
            <button onClick={saveCreds} disabled={savingCreds} data-testid="cred-save-btn"
              className="mt-4 w-full rounded-xl bg-shipany-blue py-3 text-sm font-bold text-white hover:bg-[#0648B3] disabled:opacity-60">
              {savingCreds ? "Verifying live…" : "Save & Verify"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
